import {
  COUPON_FORM_STEPS,
  COUPON_FORM_VIEW_SUBTITLES,
  COUPON_PREVIEW_NOT_SET,
  DISCOUNT_TYPE_OPTIONS,
  PLATFORM_ENUM_OPTIONS,
  ZERO,
} from "constants/coupons";
import {
  formatAmountWithCurrencyPrefix,
  getCurrencyBlockTitle,
  isFlatDiscount,
  isPercentageDiscount,
} from "helpers/couponDiscountType";
import {
  formatDiscountFieldValue,
  getPreviewDateValue,
  getPreviewText,
  getStepConfig,
} from "helpers/couponFormDisplay";
import { parsePackageIds, parsePlatformIds } from "helpers/packageIds";
import { MS_PER_DAY } from "constants/queryCacheTimes";

const mapPackageDisplayName = (packageEntry) =>
  packageEntry.displayName ||
  packageEntry.packageName ||
  `Package ${packageEntry.packageId}`;

export const getCouponFormStepSubtitle = (stepId, isView) =>
  isView
    ? COUPON_FORM_VIEW_SUBTITLES[stepId] ??
      getStepConfig(stepId, COUPON_FORM_STEPS).subtitle
    : getStepConfig(stepId, COUPON_FORM_STEPS).subtitle;

export const getDiscountTypeLabel = (discountType) => {
  if (!discountType) {
    return COUPON_PREVIEW_NOT_SET;
  }

  return (
    DISCOUNT_TYPE_OPTIONS.find((option) => option.id === discountType)?.label ??
    discountType
  );
};

export const getCouponValidityDays = (effectiveDate, expiryDate) => {
  const start = new Date(effectiveDate);
  const end = new Date(expiryDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  const dayDifference = Math.ceil((end - start) / MS_PER_DAY);
  return dayDifference >= ZERO ? dayDifference : null;
};

export const buildPreviewPackageData = ({
  values,
  isCreate,
  isEdit,
  basicPackages,
}) => {
  const canResolvePackageNames =
    (isCreate || isEdit) && basicPackages.length > ZERO;

  if (values.selectAllPackages) {
    const allItems = canResolvePackageNames
      ? basicPackages.map(mapPackageDisplayName)
      : [];

    return {
      isAll: true,
      count: canResolvePackageNames
        ? basicPackages.length
        : values.packagesCount ?? null,
      items: allItems,
    };
  }

  const packageIds = parsePackageIds(values.packageIds);
  if (packageIds.length > ZERO) {
    return {
      isAll: false,
      items: packageIds.map(
        (packageId) =>
          basicPackages.find(
            (packageEntry) => packageEntry.packageId === packageId
          )?.displayName ?? `Package ${packageId}`
      ),
    };
  }

  if (values.packagesCount) {
    return {
      isAll: false,
      items: [`${values.packagesCount} packages selected`],
    };
  }

  return { isAll: false, items: [] };
};

export const buildPreviewPlatformItems = (values) => {
  if (values.selectAllPlatforms) {
    return PLATFORM_ENUM_OPTIONS.map((platform) => platform.label);
  }

  const platformIds = parsePlatformIds(values.platformIds);
  if (platformIds.length > ZERO) {
    return platformIds.map(
      (platformId) =>
        PLATFORM_ENUM_OPTIONS.find(
          (platform) => platform.platformId === platformId
        )?.label ?? `Platform ${platformId}`
    );
  }

  return [];
};

export const buildValidityPreviewItems = (effectiveDate, expiryDate) => [
  {
    label: "Effective Date",
    value: getPreviewDateValue(effectiveDate),
  },
  {
    label: "Expiry Date",
    value: getPreviewDateValue(expiryDate),
  },
];

export const buildDiscountPreviewItems = (
  values,
  currencyOptions = [],
  selectedCurrencyIds = []
) => {
  const items = [
    {
      label: "Type",
      value: getDiscountTypeLabel(values.discountType),
    },
  ];

  if (isPercentageDiscount(values.discountType)) {
    items.push({
      label: "Value",
      value: formatDiscountFieldValue(values, currencyOptions),
    });
  }

  const currencySettings = values.currencySettings ?? {};
  const currencyIds =
    selectedCurrencyIds.length > ZERO
      ? selectedCurrencyIds
      : Object.keys(currencySettings);

  if (currencyIds.length === ZERO) {
    if (isFlatDiscount(values.discountType)) {
      items.push({
        label: "Amounts",
        value: COUPON_PREVIEW_NOT_SET,
      });
    }
    return items;
  }

  currencyIds.forEach((currencyId) => {
    const settings = currencySettings[currencyId] ?? {};
    const currencyTitle = getCurrencyBlockTitle(currencyOptions, currencyId);

    if (isFlatDiscount(values.discountType)) {
      items.push({
        label: `${currencyTitle} flat amount`,
        value:
          formatAmountWithCurrencyPrefix(
            settings.discountAmount,
            currencyOptions,
            currencyId
          ) || COUPON_PREVIEW_NOT_SET,
      });
    }

    if (isPercentageDiscount(values.discountType)) {
      items.push({
        label: `${currencyTitle} max discount`,
        value:
          formatAmountWithCurrencyPrefix(
            settings.maxAllowedAmount,
            currencyOptions,
            currencyId
          ) || COUPON_PREVIEW_NOT_SET,
      });
    }

    items.push(
      {
        label: `${currencyTitle} min package MRP`,
        value:
          formatAmountWithCurrencyPrefix(
            settings.minimumPackageMrp,
            currencyOptions,
            currencyId
          ) || COUPON_PREVIEW_NOT_SET,
      },
      {
        label: `${currencyTitle} min cart MRP`,
        value:
          formatAmountWithCurrencyPrefix(
            settings.minimumCartMrp,
            currencyOptions,
            currencyId
          ) || COUPON_PREVIEW_NOT_SET,
      }
    );
  });

  return items;
};

export const buildUsagePreviewItems = (values, { isEdit, isView }) => {
  const items = [
    {
      label: "Total coupon count",
      value: getPreviewText(values.totalCouponCount),
    },
    {
      label: "Applicable multiple times",
      value: values.isApplicableMultipleTimes ? "Yes" : "No",
    },
  ];

  if (isEdit || isView) {
    const usedCount = values.usedCount ?? ZERO;
    items.splice(1, ZERO, {
      label: "Times used",
      value: `${usedCount} / ${getPreviewText(values.totalCouponCount)}`,
    });
  }

  if (!isEdit) {
    items.push({
      label: "Single user only",
      value: values.isApplicableForSingleUser ? "Yes" : "No",
    });

    if (values.isApplicableForSingleUser) {
      items.push({
        label: "UAM ID",
        value: getPreviewText(values.uamId),
      });
    }
  }

  return items;
};
